import * as React from 'react';

import * as Sentry from '@sentry/react';

import { setLocation } from 'js/lib/coursera.redirect';
import { useLocation } from 'js/lib/useRouter';
import { get as getUser, isAuthenticatedUser } from 'js/lib/user';

import { useNaptime } from 'bundles/naptimejs';
import ProgramSwitcherSelectionsV1 from 'bundles/naptimejs/resources/programSwitcherSelections.v1';
import { ENTERPRISE_MODAL_NAMES } from 'bundles/unified-common/constants/enterprise';
import { useEnterpriseAutoEnrolledCourses } from 'bundles/unified-common/contexts/EnterpriseAutoEnrolledCoursesContext';
import { useEnterpriseModalManager } from 'bundles/unified-common/contexts/EnterpriseModalManagerContext';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useProgram, useProgramMembership } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useEnterpriseCoursePageData } from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseBffCourseContext';
import { useEnterpriseSpecializationPageData } from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseBffSpecializationContext';
import EnterpriseLearnerFlowOptimizationContext from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseLearnerFlowOptimizationContext';
import useContinueWithCourse from 'bundles/unified-description-page-common/hooks/useContinueWithCourse';
import useContinueWithSpecialization from 'bundles/unified-description-page-common/hooks/useContinueWithSpecialization';
import useEnterpriseCourseEnrollment from 'bundles/unified-description-page-common/hooks/useEnterpriseCourseEnrollment';
import useGuidedCourseNextStepsLazy from 'bundles/unified-description-page-common/hooks/useGuidedCourseNextStepsLazy';
import { useEnterpriseAdminEnrollment } from 'bundles/unified-description-page-common/providers/enterprise/EnterpriseAdminEnrollmentDataProvider';
import {
  addExternalPlatformTracking,
  constructDeepLinkUrl,
  getCourseProductUrl,
} from 'bundles/unified-description-page-common/utils';

export const EnterpriseLearnerFlowOptimizationDataProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const features = useEnterpriseProductFeatures();
  const programAutoEnrollmentData = useEnterpriseAutoEnrolledCourses();
  const coursePageData = useEnterpriseCoursePageData();
  const specializationPageData = useEnterpriseSpecializationPageData();
  const program = useProgram();
  const { membership } = useProgramMembership();
  const modalManager = useEnterpriseModalManager();
  const { naptime } = useNaptime(() => ({}));
  const { isExternal, deepLinkItemId } = location.query;
  const { fetchNextSteps } = useGuidedCourseNextStepsLazy();
  const userId = getUser().id;
  const firstCourseSlug = coursePageData?.slug || specializationPageData?.courses?.[0]?.slug;
  const { isAdminEnrollmentEnabled: isGlobalAutoEnrollmentEnabled } = useEnterpriseAdminEnrollment();
  const { shouldShowGoToCourseButton, isLoading: isContinueButtonLoading } = useContinueWithCourse();
  const {
    shouldShowGoToCourseButton: shouldShowGoToCourseButtonForSpecialization,
    isLoading: isContinueButtonLoadingForSpecialization,
  } = useContinueWithSpecialization();
  const [navigationUrl, setNavigationUrl] = React.useState<string | null>(null);
  const [isTrackingComplete, setTrackingComplete] = React.useState<boolean>(false);

  const isComingFromExternalPlatforms = isExternal === 'true'; // eg. LMS/LXPs
  const programMembershipInstance = membership;
  const isEligibleToJoinProgram = programMembershipInstance?.isEligibleToJoinProgram;
  const productId = coursePageData?.id ? coursePageData.id : (specializationPageData?.id as string);
  const isAuthenticatedAndComingFromExternalPlatforms = isAuthenticatedUser() && isComingFromExternalPlatforms;
  const showOptimizedLearnerFlow =
    !!features?.enableOptimizedLearnerFlow && isAuthenticatedAndComingFromExternalPlatforms;
  const isCourseIncludedInAutoEnrollTrigger = programAutoEnrollmentData?.map((_) => _.id)?.includes(productId);
  const showOptimisedAutoEnrollmentFlowV1 = showOptimizedLearnerFlow && isCourseIncludedInAutoEnrollTrigger;
  const showOptimisedAutoEnrollmentFlowV2 =
    isGlobalAutoEnrollmentEnabled && isAuthenticatedAndComingFromExternalPlatforms;
  const acceptedTermsOfUse =
    !modalManager?.isLoading && modalManager?.modalToShow !== ENTERPRISE_MODAL_NAMES.ENTERPRISE_TERMS_OF_USE;

  const isAlreadyPartOfCourseOrSpecialization =
    programMembershipInstance?.isProgramMember && coursePageData
      ? shouldShowGoToCourseButton && !isContinueButtonLoading
      : shouldShowGoToCourseButtonForSpecialization && !isContinueButtonLoadingForSpecialization;

  // wating for all data to be ready before sending tracking event
  const isLoading = isContinueButtonLoading || isContinueButtonLoadingForSpecialization || modalManager?.isLoading;

  // In optimised autoenrollment, if the current XDP course is set be auto-enrolled with the current program (from slug),
  // We directly enroll users into the program and as a result, user gets enrolled in the course
  // Post this, we navigate the user to the next step in the course

  const isOptimisedAutoEnrollmenteV1Possible =
    isEligibleToJoinProgram &&
    showOptimisedAutoEnrollmentFlowV1 &&
    // This handles the case where we wait for user to accept TnC before starting to autoEnroll in the course.
    acceptedTermsOfUse;

  const isOptimisedAutoEnrollmentV2Possible =
    showOptimisedAutoEnrollmentFlowV2 &&
    (programMembershipInstance?.isProgramMember || programMembershipInstance?.isEligibleToJoinProgram) &&
    acceptedTermsOfUse;

  const isOptimisedAutoEnrollmentPossible = isOptimisedAutoEnrollmenteV1Possible || isOptimisedAutoEnrollmentV2Possible;
  const willAutoEnroll = isOptimisedAutoEnrollmentPossible && !isAlreadyPartOfCourseOrSpecialization && !isLoading;

  const onActionSuccess = React.useCallback<VoidFunction>(async () => {
    const baseHomeUrl = getCourseProductUrl(firstCourseSlug || '', false);
    const courseHome = `${baseHomeUrl}/home`;
    let resourcePath;

    try {
      if (deepLinkItemId && firstCourseSlug) {
        resourcePath = constructDeepLinkUrl({
          courseSlug: firstCourseSlug,
          deepLinkItemId,
          isExternal: isComingFromExternalPlatforms,
        });
      } else {
        resourcePath = await fetchNextSteps(firstCourseSlug);
        resourcePath = addExternalPlatformTracking(resourcePath || courseHome);
      }

      await naptime.executeMutation(
        ProgramSwitcherSelectionsV1.update(userId, {
          selectionType: 'PROGRAM',
          programId: program?.id,
        })
      );
    } catch (err) {
      Sentry?.captureException(err, {
        extra: {
          message: '[Learner flow optimization] Error navigating to next item after auto enrollment',
          deepLinkItemId,
          firstCourseSlug,
        },
      });
    } finally {
      const url = resourcePath || courseHome;
      setNavigationUrl(url);

      // If no tracking is needed (e.g., direct enrollment), mark as complete
      if (!isComingFromExternalPlatforms) {
        setTrackingComplete(true);
      }
    }

    // naptime reference changes multiple times during re-renders, which results into this useCallback re-executing again.
    // Which results into cascade effect of useCallbacks re-executing down the line.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchNextSteps, firstCourseSlug, program?.id, isComingFromExternalPlatforms, deepLinkItemId]);

  const [enrollIntoCourse, { error, programJoinErrorCode, isEnrolling }] =
    useEnterpriseCourseEnrollment(onActionSuccess);

  // Redirect to next item when both URL is set and tracking is complete
  React.useEffect(() => {
    if (navigationUrl && isTrackingComplete) {
      setLocation(navigationUrl);
    }
  }, [navigationUrl, isTrackingComplete]);

  // Every time there is dependency update in theReact.useLayoutEffect, it runs before the browser paints the screen.
  // This helps us set the isLoading faster than useEffect - which runs after render might result into flickering loading screen.
  React.useLayoutEffect(() => {
    if (willAutoEnroll) {
      enrollIntoCourse({ isAutoEnrollment: true });
    }
  }, [willAutoEnroll, enrollIntoCourse]);

  return (
    <EnterpriseLearnerFlowOptimizationContext.Provider
      value={{
        error, // contains any other errors that are not covered by programJoinErrorCode
        isEnrolling,
        programJoinErrorCode,
        showOptimizedLearnerFlow: showOptimizedLearnerFlow || showOptimisedAutoEnrollmentFlowV2,
        enrollIntoCourse,
        onJoiningSuccess: onActionSuccess,
        navigationUrl,
        setNavigationUrl,
        isTrackingComplete,
        setTrackingComplete,
        isLoadingLfo: isLoading,
        deepLinkItemId,
        courseSlug: firstCourseSlug,
      }}
    >
      {children}
    </EnterpriseLearnerFlowOptimizationContext.Provider>
  );
};
